import { AuthTokenPayload } from "@vertesia/common"
import { jwtDecode } from "jwt-decode"

import { CONFIGURATION } from "@env/variables"
import { firebaseAuth, getFirebaseAuthToken } from "@/plugins/session/auth/firebase"
import { LastSelectedAccountId_KEY, LastSelectedProjectId_KEY } from "@session/UserSession"

let AUTH_TOKEN_RAW: string | undefined
let AUTH_TOKEN: AuthTokenPayload | undefined

interface ComposableTokenResponse {
    rawToken: string
    token: AuthTokenPayload
    error: boolean
    message?: string
}

export async function fetchComposableToken(getIdToken: () => Promise<string | null | undefined>, accountId?: string, projectId?: string, ttl?: number): Promise<string> {
    const idToken = await getIdToken()
    if (!idToken) {
        throw new Error("No id token found")
    }

    try {
        // Call STS to generate a user token
        const stsUrl = new URL("https://sts.vertesia.io/token/issue")
        const requestBody = {
            type: 'user',
            account_id: accountId,
            project_id: projectId,
            expires_at: ttl ? Math.floor(Date.now() / 1000) + ttl : undefined,
        }

        const stsRes = await fetch(stsUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify(requestBody)
        })

        if (idToken && stsRes?.status === 412) {
            const idTokenDecoded = jwtDecode(idToken) as any
            if (!idTokenDecoded?.email) {
                throw new Error('No email found in id token')
            }
            throw new UserNotFoundError('User not found', idTokenDecoded.email)
        }

        if (!stsRes.ok) {
            const errorText = await stsRes.text()
            throw new Error(`Failed to get token from STS: ${stsRes.status}`)
        }

        const { token } = await stsRes.json()
        return token

    } catch (error) {
        if (error instanceof UserNotFoundError) {
            throw error
        }

        localStorage.removeItem(LastSelectedAccountId_KEY)
        localStorage.removeItem(LastSelectedProjectId_KEY)
        throw new Error('Failed to get composable token')
    }
}

export async function fetchComposableTokenFromFirebaseToken(accountId?: string, projectId?: string, ttl?: number) {
    return fetchComposableToken(getFirebaseAuthToken, accountId, projectId, ttl)
}

export async function getComposableToken(accountId?: string, projectId?: string, initToken?: string, forceRefresh = false): Promise<ComposableTokenResponse> {
    const selectedAccount = accountId ?? localStorage.getItem(LastSelectedAccountId_KEY) ?? undefined
    const selectedProject = projectId ?? localStorage.getItem(LastSelectedProjectId_KEY) ?? undefined

    if (!forceRefresh && AUTH_TOKEN_RAW && AUTH_TOKEN && AUTH_TOKEN.exp > (Date.now() / 1000 + 300)) {
        return { rawToken: AUTH_TOKEN_RAW, token: AUTH_TOKEN, error: false }
    }

    if (firebaseAuth.currentUser) {
        AUTH_TOKEN_RAW = await fetchComposableTokenFromFirebaseToken(selectedAccount, selectedProject)
    } else if (initToken || AUTH_TOKEN_RAW) {
        AUTH_TOKEN_RAW = await fetchComposableToken(() => Promise.resolve(initToken ?? AUTH_TOKEN_RAW), selectedAccount, selectedProject)
    }

    if (!AUTH_TOKEN_RAW) {
        throw new Error("Cannnot acquire a composable token")
    }

    AUTH_TOKEN = jwtDecode(AUTH_TOKEN_RAW)

    if (!AUTH_TOKEN?.exp || !AUTH_TOKEN_RAW) {
        throw new Error("Invalid composable token")
    }

    return { rawToken: AUTH_TOKEN_RAW, token: AUTH_TOKEN, error: false }
}

export class UserNotFoundError extends Error {
    email: string
    constructor(message: string, email: string) {
        super(message)
        this.name = "UserNotFoundError"
        this.email = email
    }
}
